import React, { useCallback, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  SwipeableDrawer,
  Dialog,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ShareIcon from "@mui/icons-material/Share";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import VerifiedIcon from "@mui/icons-material/Verified";
import MedicalStory from "../../components/common/MedicalCampaignDetailPageComponents/MedicalStory";
import DocumentSection from "../../components/common/CampaignDetailPageComponents/Document";
import Updates from "../../components/common/CampaignDetailPageComponents/Updates";
import Comments from "../../components/common/CampaignDetailPageComponents/Comments";
import Donors from "../../components/common/CampaignDetailPageComponents/Donors";
import UrgentNeedsCampaign from "../../components/common/MedicalCampaignDetailPageComponents/UrgentNeedsCampaign";
import Faq from "../../components/common/CampaignDetailPageComponents/Faq";
import NewsLogoSection from "../HomePage/NewsLogoSection";
import CampaignPopup from "../../components/common/CampaignDetailPageComponents/CampaignPopup";
import ClosedCampaign from "../../components/common/CampaignDetailPageComponents/ClosedCampaign";
import DonationBoxV2 from "../../components/common/DonationBoxV2";
import HowToDonateV2 from "../../components/common/CampaignDetailPageComponents/HowToDonateV2";
import ShareLink from "../../components/common/ShareLink";
import { getCampaignShareDetails } from "../../api/services/campaignService";
import { getCampaignUrl } from "../../utils/slugUtils";
import { Images } from "../../utils/Images";

const BRAND_RED = "#F04951";
const PROGRESS_RED = "#EC1B26";
// iOS needs different SwipeableDrawer transition handling for a smooth gesture.
const iOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);
// Active section-tab underline (Figma: node 570:4409 border-b) — a lighter
// coral red, distinct from the deeper PROGRESS_RED used by the goal bar.
const TAB_UNDERLINE_RED = "#F04951";
// Verified-badge green from the Figma design
const VERIFIED_GREEN = "#12B76A";

// Full amount in Indian grouping (₹20,00,000), like the campaign page — not ₹20L.
const formatCurrency = (amount) =>
  `₹${Math.round(Number(amount) || 0).toLocaleString("en-IN")}`;

const calculateProgress = (raised, goal) =>
  goal > 0 ? Math.min(Math.round((raised / goal) * 100), 100) : 0;

/** Hospital building icon per the Figma design (building + cross + door) */
const HospitalIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4.2" y="3.2" width="15.6" height="17.6" rx="1.6" stroke="#1D1D1B" strokeWidth="1.6" />
    <path d="M12 6.4v4.2M9.9 8.5h4.2" stroke="#1D1D1B" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M9.6 20.8v-3.9c0-.72.58-1.3 1.3-1.3h2.2c.72 0 1.3.58 1.3 1.3v3.9" stroke="#1D1D1B" strokeWidth="1.6" />
    <path d="M2.6 20.8h18.8" stroke="#1D1D1B" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const InfoIconContainer = ({ children }) => (
  <Box
    sx={{
      width: "39px",
      height: "39px",
      background: "rgba(11,18,21,0.06)",
      borderRadius: "7.2px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

const StatsIconContainer = ({ children, isMobile }) => (
  <Box
    sx={{
      width: isMobile ? "40px" : "50px",
      height: isMobile ? "40px" : "50px",
      background: "rgba(242,178,186,0.2)",
      border: "0.37px solid #D1DFAD",
      borderRadius: "11px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

// Section tabs (Figma): Story | Document | Updates | Comments — same icon assets the
// campaign detail page sidebar uses (Images.box/layer/refresh/messages)
const SECTION_TABS = [
  { key: "story", label: "Story", icon: Images.box },
  { key: "document", label: "Document", icon: Images.layer },
  { key: "updates", label: "Updates", icon: Images.refresh },
  { key: "comments", label: "Comments", icon: Images.messages },
];

const LifeDetailPageV2 = ({ campaignData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isDesktop = useMediaQuery("(min-width: 1001px)");

  const [donationSheetOpen, setDonationSheetOpen] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [donationAmount, setDonationAmount] = useState(3000);
  const [activeSection, setActiveSection] = useState("story");
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareInfo, setShareInfo] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState(null);
  const handleClosePopup = useCallback(() => setShowPopup(false), []);

  const handleSectionTabClick = useCallback((key) => {
    setActiveSection(key);
    document.getElementById(key)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // Scroll-spy: highlight the section currently in view (same behavior as CampaignSidebar)
  React.useEffect(() => {
    let scrollTimeoutId = null;
    const updateActive = () => {
      let best = null;
      let bestTop = -Infinity;
      SECTION_TABS.forEach(({ key }) => {
        const el = document.getElementById(key);
        if (!el) return;
        const top = el.getBoundingClientRect().top;
        // The active section is the last one whose top has passed the header line
        if (top <= 140 && top > bestTop) {
          best = key;
          bestTop = top;
        }
      });
      if (best) setActiveSection(best);
    };
    const onScroll = () => {
      if (scrollTimeoutId) clearTimeout(scrollTimeoutId);
      scrollTimeoutId = setTimeout(updateActive, 80);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (scrollTimeoutId) clearTimeout(scrollTimeoutId);
    };
  }, []);

  if (Number(campaignData?.Closedcampaign) === 1) {
    return <ClosedCampaign campaignData={campaignData} hideSimilarCampaigns />;
  }

  const {
    id,
    title = "",
    description = "",
    bannerImage,
    image,
    taxBenefit,
    goalAmount = 0,
    raisedAmount = 0,
    backers = 0,
    hide_goal = 0,
    hideAll = 0,
    medical_details = {},
  } = campaignData || {};

  const heroImage = bannerImage || image;
  const showProgress = Number(hideAll) !== 1;
  const progressPct = calculateProgress(raisedAmount, goalAmount);

  const beneficiaryName = medical_details?.beneficiary_name?.trim() || "";
  const campaignerName = medical_details?.campaigner_name?.trim() || "";
  const hospitalAddress = medical_details?.hospital_address?.trim() || "";
  const hospitalLink = medical_details?.hospital_link?.trim() || "";
  const hasPatientInfo = beneficiaryName || campaignerName || hospitalAddress;

  const fallbackShareUrl =
    (typeof window !== "undefined" && window.location?.origin && campaignData
      ? (() => {
          const url = getCampaignUrl(campaignData);
          return url ? `${window.location.origin}${url}` : "";
        })()
      : "") || "";

  const fetchShareDetails = useCallback(async () => {
    if (!id) return;
    setShareLoading(true);
    setShareError(null);
    try {
      const data = await getCampaignShareDetails(id);
      setShareInfo(data);
    } catch (error) {
      console.error("Failed to fetch share details:", error);
      setShareError("Unable to load share options right now.");
    } finally {
      setShareLoading(false);
    }
  }, [id]);

  const handleShareClick = () => {
    if (!shareInfo && !shareLoading) {
      fetchShareDetails();
    }
    setShowShareDialog(true);
  };

  const handleCloseShareDialog = () => setShowShareDialog(false);

  const chipLeftPct =
    progressPct <= 5 ? 0 : progressPct >= 92 ? 84 : progressPct - 4;

  // Progress-card stat icon: same icons as the campaign cards
  // (Images.cardGoal/cardRaised/cardBackers) inside the rounded container from
  // the Figma design (node 168:5988) — 50px box, faint fill + soft pink border.
  const statIconBoxSx = {
    width: isMobile ? "30px" : "50px",
    height: isMobile ? "30px" : "50px",
    borderRadius: isMobile ? "7px" : "11px",
    background: "rgba(206,204,204,0.1)",
    border: "0.37px solid #C1838A",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    // Very small phones: drop the icons so the full amounts (₹9,00,00,000) still fit.
    "@media (max-width:340px)": { display: "none" },
  };
  const statIconImgSx = {
    width: isMobile ? "16px" : "27px",
    height: isMobile ? "16px" : "27px",
    objectFit: "contain",
  };

  return (
    <>
      {/* ── HERO IMAGE ── */}
      <Box
        sx={{
          width: isMobile ? "100%" : "94.44%",
          maxWidth: "1440px",
          margin: "0 auto",
          mt: isMobile ? "16px" : "40px",
        }}
      >
        <Box
          sx={{
            position: "relative",
            // Mobile: inset 16px each side (aligned with the rest of the content)
            // and keep the rounded corners — matches the V1 campaign pages rather
            // than bleeding edge-to-edge.
            mx: isMobile ? "16px" : 0,
            borderRadius: "16px",
            overflow: "hidden",
            // 3:1 at every width — the banner size the admin asks for (1200x400) — so
            // the whole banner shows. A fixed height cropped its sides (only 60–85% of
            // the width was visible on desktop).
            aspectRatio: "3 / 1",
          }}
        >
          <img
            src={heroImage || Images.fallBackImageCard}
            alt={title}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />

          {taxBenefit && (
            <Box
              sx={{
                position: "absolute",
                top: isMobile ? "12px" : "20px",
                left: isMobile ? "12px" : "22px",
                background: "linear-gradient(106.41deg, #EC1B26 5.07%, #FF4A60 106.05%)",
                borderRadius: "4px",
                px: isMobile ? "14px" : "30px",
                py: isMobile ? "8px" : "16px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Red Hat Display",
                  fontWeight: 700,
                  fontSize: isMobile ? "12px" : "16px",
                  color: "#fff",
                  lineHeight: 1,
                  letterSpacing: "-0.32px",
                  whiteSpace: "nowrap",
                }}
              >
                {taxBenefit}
              </Typography>
            </Box>
          )}

          <IconButton
            onClick={handleShareClick}
            sx={{
              position: "absolute",
              top: isMobile ? "10px" : "20px",
              right: isMobile ? "10px" : "20px",
              background: "#FFFFFF",
              width: isMobile ? "36px" : "46px",
              height: isMobile ? "36px" : "48px",
              borderRadius: "8px",
              "&:hover": { background: "#F5F5F5" },
            }}
          >
            <ShareIcon sx={{ fontSize: isMobile ? "16px" : "18px", color: "#1D1D1B" }} />
          </IconButton>
        </Box>

        {/* ── PROGRESS CARD — sits just below the hero so the banner is not covered ── */}
        {showProgress && (
          <Box
            sx={{
              background: "#FFFFFF",
              borderRadius: isMobile ? "16px" : "20px",
              boxShadow: "0px 7px 3.5px rgba(0,0,0,0.09)",
              mt: isMobile ? "12px" : "20px",
              mx: isMobile ? "16px" : "50px",
              px: isMobile ? "16px" : "30px",
              pt: "24px",
              pb: "24px",
              position: "relative",
              zIndex: 2,
            }}
          >
            {/* Progress bar */}
            <Box sx={{ position: "relative", height: "28px", mb: "24px" }}>
              <Box
                sx={{
                  position: "absolute",
                  top: "6px",
                  left: 0,
                  right: 0,
                  height: "16px",
                  background: "#E8E8E8",
                  borderRadius: "20px",
                  opacity: 0.6,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: "10px",
                  left: "4px",
                  width: `calc(${progressPct}% - 4px)`,
                  height: "8px",
                  background: PROGRESS_RED,
                  borderRadius: "4px",
                  transition: "width 0.4s ease",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  // Full (or nearly full) campaigns: pin the % chip flush to the end of the bar
                  ...(progressPct >= 92 ? { right: 0 } : { left: `${chipLeftPct}%` }),
                  height: "28px",
                  background: PROGRESS_RED,
                  borderRadius: "4px",
                  px: "8px",
                  display: "flex",
                  alignItems: "center",
                  minWidth: "42px",
                  justifyContent: "center",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Red Hat Display",
                    fontWeight: 700,
                    fontSize: isMobile ? "12px" : "14px",
                    color: "#fff",
                    lineHeight: 1,
                    letterSpacing: "-0.29px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {progressPct}%
                </Typography>
              </Box>
            </Box>

            {/* Stats: GOAL | Raised | Backers */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              {Number(hide_goal) !== 1 && (
                <>
                  <Box sx={{ display: "flex", gap: isMobile ? "6px" : "14px", alignItems: "center", minWidth: 0 }}>
                    <Box sx={statIconBoxSx}>
                      <Box component="img" src={Images.progressGoal} alt="" sx={statIconImgSx} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 500, fontSize: isMobile ? "11px" : "16px", color: "#686866", lineHeight: "18.4px", textTransform: "capitalize" }}>
                        Goal
                      </Typography>
                      <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 700, fontSize: isMobile ? "clamp(11px, 3.4vw, 14px)" : "16px", color: "#1D1D1B", lineHeight: "18.4px", whiteSpace: "nowrap" }}>
                        {formatCurrency(goalAmount)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ width: "1px", height: isMobile ? "28px" : "32px", background: "#E8E8E8", flexShrink: 0, mx: isMobile ? "6px" : "16px" }} />
                </>
              )}
              {/* Raised */}
              <Box sx={{ display: "flex", gap: isMobile ? "6px" : "14px", alignItems: "center", minWidth: 0 }}>
                <Box sx={statIconBoxSx}>
                  <Box component="img" src={Images.progressRaised} alt="" sx={statIconImgSx} />
                </Box>
                <Box>
                  <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 500, fontSize: isMobile ? "11px" : "16px", color: "#686866", lineHeight: "18.4px", textTransform: "capitalize" }}>
                    Raised
                  </Typography>
                  <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 700, fontSize: isMobile ? "clamp(11px, 3.4vw, 14px)" : "16px", color: "#1D1D1B", lineHeight: "18.4px", whiteSpace: "nowrap" }}>
                    {formatCurrency(raisedAmount)}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ width: "1px", height: isMobile ? "28px" : "32px", background: "#E8E8E8", flexShrink: 0, mx: isMobile ? "6px" : "16px" }} />
              {/* Backers */}
              <Box sx={{ display: "flex", gap: isMobile ? "6px" : "14px", alignItems: "center", minWidth: 0 }}>
                <Box sx={statIconBoxSx}>
                  <Box component="img" src={Images.progressBackers} alt="" sx={statIconImgSx} />
                </Box>
                <Box>
                  <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 500, fontSize: isMobile ? "11px" : "16px", color: "#686866", lineHeight: "18.4px", textTransform: "capitalize" }}>
                    Backers
                  </Typography>
                  <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 700, fontSize: isMobile ? "clamp(11px, 3.4vw, 14px)" : "16px", color: "#1D1D1B", lineHeight: "18.4px", whiteSpace: "nowrap" }}>
                    {Number(backers).toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* ── TWO-COLUMN LAYOUT — sidebar sticky ends here naturally ── */}
      <Box
        sx={{
          width: isMobile ? "100%" : "94.44%",
          maxWidth: "1440px",
          margin: "0 auto",
          mt: isMobile ? "24px" : "30px",
          px: isMobile ? "16px" : 0,
          display: "flex",
          flexDirection: isDesktop ? "row" : "column",
          gap: isDesktop ? "30px" : 0,
          // Desktop (row): top-align so the sticky sidebar pins from the top.
          // Mobile/tablet (column): stretch so the content panel fills the
          // viewport width instead of shrinking to its widest child (the tab
          // bar), which would push the page wider than the screen.
          alignItems: isDesktop ? "flex-start" : "stretch",
        }}
      >
        {/* ── LEFT COLUMN: all scrollable content ── */}
        <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          {/* Title */}
          <Typography
            sx={{
              fontFamily: "Red Hat Display",
              fontWeight: 700,
              fontSize: isMobile ? "20px" : isTablet ? "26px" : "36px",
              lineHeight: 1.3,
              color: "#1D1D1B",
              mb: isMobile ? "14px" : "20px",
            }}
          >
            {title}
          </Typography>

          {/* Description */}
          <Typography
            sx={{
              fontFamily: "Red Hat Display",
              fontWeight: 400,
              fontSize: isMobile ? "14px" : "16px",
              lineHeight: "1.7",
              color: "#4A4A49",
              mb: isMobile ? "20px" : "28px",
            }}
          >
            {description}
          </Typography>

          {/* Patient info cards */}
          {hasPatientInfo && (
            <Box
              sx={{
                display: "flex",
                gap: "10px",
                flexWrap: isMobile ? "wrap" : "nowrap",
                mb: isMobile ? "20px" : "30px",
              }}
            >
              {beneficiaryName && (
                <Box
                  sx={{
                    background: "#FFFFFF",
                    border: "1px solid #F1F1F1",
                    borderRadius: "12px",
                    px: "16px",
                    py: "18px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    flexShrink: 0,
                    width: isMobile ? "calc(50% - 5px)" : "168px",
                  }}
                >
                  <InfoIconContainer>
                    <PersonOutlineIcon sx={{ fontSize: "24px", color: "#1D1D1B" }} />
                  </InfoIconContainer>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 500, fontSize: "12px", color: "#767676", lineHeight: 1, letterSpacing: "-0.12px", mb: "8px" }}>
                      Beneficiary
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 600, fontSize: "14px", color: "#0B1215", lineHeight: 1, letterSpacing: "-0.14px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {beneficiaryName}
                      </Typography>
                      <VerifiedIcon sx={{ fontSize: "16px", color: VERIFIED_GREEN, flexShrink: 0 }} />
                    </Box>
                  </Box>
                </Box>
              )}

              {campaignerName && (
                <Box
                  sx={{
                    background: "#FFFFFF",
                    border: "1px solid #F1F1F1",
                    borderRadius: "12px",
                    px: "16px",
                    py: "18px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    flexShrink: 0,
                    width: isMobile ? "calc(50% - 5px)" : "168px",
                  }}
                >
                  <InfoIconContainer>
                    <PersonOutlineIcon sx={{ fontSize: "24px", color: "#1D1D1B" }} />
                  </InfoIconContainer>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 500, fontSize: "12px", color: "#767676", lineHeight: 1, letterSpacing: "-0.12px", mb: "8px" }}>
                      Campaigner
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 600, fontSize: "14px", color: "#0B1215", lineHeight: 1, letterSpacing: "-0.14px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {campaignerName}
                      </Typography>
                      <VerifiedIcon sx={{ fontSize: "16px", color: VERIFIED_GREEN, flexShrink: 0 }} />
                    </Box>
                  </Box>
                </Box>
              )}

              {hospitalAddress && (
                <Box
                  sx={{
                    background: "#FFFFFF",
                    border: "1px solid #F1F1F1",
                    borderRadius: "12px",
                    px: "20px",
                    py: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flex: 1,
                    minWidth: isMobile ? "100%" : 0,
                    cursor: hospitalLink ? "pointer" : "default",
                  }}
                  onClick={() => hospitalLink && window.open(hospitalLink, "_blank")}
                >
                  <Box sx={{ display: "flex", gap: "12px", alignItems: "center", minWidth: 0 }}>
                    <InfoIconContainer>
                      <HospitalIcon />
                    </InfoIconContainer>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 500, fontSize: "12px", color: "#767676", lineHeight: 1, letterSpacing: "-0.12px", mb: "8px" }}>
                        Hospitalized at
                      </Typography>
                      <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 600, fontSize: "14px", color: "#0B1215", lineHeight: 1, letterSpacing: "-0.14px" }}>
                        {hospitalAddress}
                      </Typography>
                    </Box>
                  </Box>
                  <GpsFixedIcon sx={{ fontSize: "24px", color: "#1D1D1B", flexShrink: 0, ml: "12px" }} />
                </Box>
              )}
            </Box>
          )}

          {/* ── Section tabs: Story | Document | Updates | Comments (Figma) ── */}
          <Box
            sx={{
              background: "#FFFFFF",
              borderRadius: "12px",
              border: "1px solid #F1F1F1",
              boxShadow: "0px 4px 14px rgba(0,0,0,0.06)",
              pt: isMobile ? "14px" : "30px",
              pb: isMobile ? "10px" : "20px",
              px: isMobile ? "6px" : "30px",
              display: "flex",
              alignItems: "flex-start",
              // Desktop keeps the Figma spacing; mobile spreads the tabs evenly
              // so they fill the row (like CampaignTopBar on the other pages).
              justifyContent: isMobile ? "space-evenly" : "space-between",
              mb: "30px",
              // Fallback only — on mobile the tabs flex to fit, so this rarely
              // engages, but very narrow screens can still scroll the row.
              overflowX: "auto",
            }}
          >
            {SECTION_TABS.map(({ key, label, icon }) => {
              const isActive = activeSection === key;
              return (
                <Box
                  key={key}
                  onClick={() => handleSectionTabClick(key)}
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    // Mobile: center content, on desktop keep the Figma left inset.
                    justifyContent: isMobile ? "center" : "flex-start",
                    gap: isMobile ? "6px" : "12px",
                    height: isMobile ? "46px" : "60px",
                    px: isMobile ? "4px" : "28px",
                    cursor: "pointer",
                    userSelect: "none",
                    // Mobile: each tab flexes to an equal share and can shrink so
                    // all four fit the row. Desktop: natural width (Figma design).
                    flex: isMobile ? "1 1 0" : "0 0 auto",
                    minWidth: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={icon}
                    alt={label}
                    sx={{
                      width: isMobile ? "16px" : "26px",
                      height: isMobile ? "16px" : "26px",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{
                      fontFamily: "Red Hat Display",
                      fontWeight: isActive ? 600 : 400,
                      fontSize: isMobile ? "12px" : "18px",
                      color: isActive ? "#1D1D1B" : "#686866",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      lineHeight: 1,
                    }}
                  >
                    {label}
                  </Typography>
                  {/* Active underline — Figma border-b-3 spanning the full tab,
                      flush with the tab's bottom edge (node 570:4409) */}
                  {isActive && (
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        width: "100%",
                        height: "3px",
                        borderRadius: "2px",
                        background: TAB_UNDERLINE_RED,
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Medical Story */}
          <Box id="story" sx={{ mb: "30px", scrollMarginTop: "130px" }}>
            <MedicalStory showPatientHeader={false} showFullContentByDefault={true} campaignData={campaignData} />
          </Box>

          {/* Documents */}
          <Box id="document" sx={{ mb: "30px", scrollMarginTop: "130px" }}>
            <DocumentSection campaignData={campaignData} />
          </Box>

          {/* Updates */}
          <Box id="updates" sx={{ mb: "30px", scrollMarginTop: "130px" }}>
            <Updates campaignData={campaignData} />
          </Box>

          {/* Comments */}
          <Box id="comments" sx={{ mb: "30px", scrollMarginTop: "130px" }}>
            <Comments campaignData={campaignData} />
          </Box>

          {/* Donors */}
          <Box sx={{ mb: "30px" }}>
            <Donors campaignData={campaignData} />
          </Box>
        </Box>

        {/* ── RIGHT SIDEBAR — sticky, naturally stops when parent (two-column) ends ── */}
        {isDesktop && (
          <Box
            sx={{
              width: "537px",
              flexShrink: 0,
              position: "sticky",
              top: "20px",
              alignSelf: "flex-start",
            }}
          >
            <DonationBoxV2
              campaignTitle={title}
              campaignId={id}
              beneficiaryName={beneficiaryName}
              campaignData={campaignData}
              onDonationAmountChange={setDonationAmount}
            />
          </Box>
        )}
      </Box>

      {/* ── FULL-WIDTH SECTIONS (below sticky boundary) ── */}
      <Box
        sx={{
          width: isMobile ? "100%" : "94.44%",
          maxWidth: "1440px",
          margin: "0 auto",
          px: isMobile ? "16px" : 0,
        }}
      >
        <Box sx={{ mb: "15px" }}>
          <UrgentNeedsCampaign campaignData={campaignData} />
        </Box>
        {/* MOBILE / TABLET: How To Donate video, after Urgent Needs. On desktop it sits inside the
            sidebar DonationBoxV2; below desktop the box only lives in the bottom sheet, which hides
            the video, so it is shown here in the page instead. */}
        {!isDesktop && (
          <Box sx={{ mb: "15px" }}>
            <HowToDonateV2 />
          </Box>
        )}
        <Box sx={{ mb: isMobile ? 0 : "15px" }}>
          <Faq />
        </Box>
      </Box>

      {!isMobile && <NewsLogoSection />}

      <CampaignPopup isOpen={showPopup} onClose={handleClosePopup} campaignData={campaignData} />

      {/* Share Dialog — same pattern as ReferFriend's "Share now" */}
      <Dialog
        open={showShareDialog}
        onClose={handleCloseShareDialog}
        maxWidth={isMobile ? "xs" : "md"}
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
            margin: isMobile ? "16px" : "auto",
            maxHeight: isMobile ? "calc(100vh - 32px)" : "90vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            ...(isMobile && {
              width: "calc(100% - 32px)",
              maxWidth: "100%",
            }),
          },
        }}
        sx={{
          "& .MuiDialog-container": {
            alignItems: "center",
            justifyContent: "center",
          },
        }}
      >
        <ShareLink
          shareData={shareInfo}
          fallbackUrl={fallbackShareUrl}
          isLoading={shareLoading}
          error={shareError}
          onRetry={fetchShareDetails}
          onClose={handleCloseShareDialog}
        />
      </Dialog>

      {/* ── MOBILE: sticky Donate Now + Bottom Sheet ── */}
      {!isDesktop && (
        <>
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1000,
              background: "#FFFFFF",
              borderTop: "1px solid #F0F0F0",
              padding: "12px 16px",
              paddingBottom: "calc(12px + env(safe-area-inset-bottom))",
            }}
          >
            <Box
              onClick={() => setDonationSheetOpen(true)}
              sx={{
                width: "100%",
                height: "52px",
                background: BRAND_RED,
                borderRadius: "11px",
                boxShadow: "0px 4px 0px #141414",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                fontFamily: "Red Hat Display",
                fontWeight: 700,
                fontSize: "16px",
                color: "#FFFFFF",
                userSelect: "none",
              }}
            >
              Donate Now
            </Box>
          </Box>

          <SwipeableDrawer
            anchor="bottom"
            open={donationSheetOpen}
            onClose={() => setDonationSheetOpen(false)}
            onOpen={() => setDonationSheetOpen(true)}
            disableSwipeToOpen
            disableBackdropTransition={!iOS}
            disableDiscovery={iOS}
            PaperProps={{
              sx: {
                borderTopLeftRadius: "20px",
                borderTopRightRadius: "20px",
                maxHeight: "92vh",
                overflow: "auto",
              },
            }}
          >
            {/* Drag handle — swipe this (or the sheet) down to dismiss */}
            <Box
              sx={{
                pt: "10px",
                pb: "6px",
                display: "flex",
                justifyContent: "center",
                cursor: "grab",
                touchAction: "none",
                position: "sticky",
                top: 0,
                background: "#FFFFFF",
                zIndex: 1,
                borderTopLeftRadius: "20px",
                borderTopRightRadius: "20px",
              }}
            >
              <Box sx={{ width: "40px", height: "4px", background: "#E0E0E0", borderRadius: "2px" }} />
            </Box>
            <Box sx={{ p: "16px", pt: "6px" }}>
              <DonationBoxV2
                campaignTitle={title}
                campaignId={id}
                beneficiaryName={beneficiaryName}
                campaignData={campaignData}
                onDonationAmountChange={setDonationAmount}
                showHowToDonate={false}
              />
            </Box>
          </SwipeableDrawer>

          <Box sx={{ height: "80px" }} />
        </>
      )}
    </>
  );
};

export default LifeDetailPageV2;
